import {Component, Input} from '@angular/core';
import {
  Aangifte,
  AangifteBerekening, AangifteRapport, BokkzAlgDataService,
  BokkzIbDataService,
  IncomeTaxEntity
} from "@snelstart-ventures/fiscaal/domain-logic";
import {AbstractControl, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SpecZorgkostenComponent } from './spec-zorgkosten/spec-zorgkosten.component';
import { SpecGiftenComponent } from './spec-giften/spec-giften.component';
import { SpecScholingComponent } from './spec-scholing/spec-scholing.component';
import { SpecOverigePgaComponent } from './spec-overige-pga/spec-overige-pga.component';
import { CommonModule } from '@angular/common';


@Component({
  selector: 'snelstart-venture-fiscaal-feature-pga-samenvatting',
  templateUrl: './pga-samenvatting.component.html',
  styleUrl: './pga-samenvatting.component.scss',

  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SpecZorgkostenComponent,
    SpecGiftenComponent,
    SpecScholingComponent,
    SpecOverigePgaComponent,
    CommonModule
  ]
})
export class PgaSamenvattingComponent {
    @Input() public id!: string
    public aangifte = {} as Aangifte;
    public incomeTaxEntity = {} as IncomeTaxEntity;
    public berekening = {} as AangifteBerekening;
    public rapport = {} as AangifteRapport;

    public optellingPga = 0;
    public totaalPgaFg = {} as FormGroup;
    public terugOntvangenPgaFg = {} as FormGroup;

    constructor(public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService, private fb: FormBuilder) {
        this.totaalPgaFg = this.fb.group({
            totaalPga: [null]
        });
        this.terugOntvangenPgaFg = this.fb.group({
            terugontvangenAlimentatie: null as unknown as number,
            inkomstenSpecifiekeZiektekosten: null as unknown as number,
            inkomstenOverigStudiekosten: null as unknown as number,
            inkomstenOverigGiften: null as unknown as number,
            terugontvangenAlimentatieAangever: null as unknown as number,
            inkomstenSpecifiekeZiektekostenAangever: null as unknown as number,
            inkomstenOverigStudiekostenAangever: null as unknown as number,
            inkomstenOverigGiftenAangever: null as unknown as number,
            terugontvangenAlimentatiePartner: null as unknown as number,
            inkomstenSpecifiekeZiektekostenPartner: null as unknown as number,
            inkomstenOverigStudiekostenPartner: null as unknown as number,
            inkomstenOverigGiftenPartner: null as unknown as number,
            totaalNegatievePga: null as unknown as number,
        });
    }

    hasGeenAftrekItems(): boolean {
        const zorgkosten = this.incomeTaxEntity.aangifte?.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten ?? {};
        const scholingskosten = this.incomeTaxEntity.aangifte?.persoonsgebondenAftrek?.pgaScholingsuitgaven ?? {};
        const giften = this.incomeTaxEntity.aangifte?.persoonsgebondenAftrek?.pgaAftrekbareGiften ?? {};
        const onderhoudsverplichtingen = this.incomeTaxEntity.aangifte?.persoonsgebondenAftrek?.pgaOnderhoudsverplichtingen ?? {};
        const weekendUitgavenGehandicapteKinderen = this.incomeTaxEntity.aangifte?.persoonsgebondenAftrek?.pgaWeekendUitgavenGehandicapteKinderen ?? {};
        const restant = this.incomeTaxEntity.aangifte?.persoonsgebondenAftrek?.restantPgaVoorgaandeJaren ?? {};
        const negatievePGA = (this.incomeTaxEntity.aangifte?.box1?.negatievePersoonsgebondenAftrek || this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.negatievePersoonsgebondenAftrek) ?? {};

        return Object.keys(zorgkosten).length < 1 &&
            Object.keys(scholingskosten).length < 1 &&
            Object.keys(giften).length < 1 &&
            Object.keys(onderhoudsverplichtingen).length < 1 &&
            Object.keys(weekendUitgavenGehandicapteKinderen).length < 1 &&
            Object.keys(restant).length < 1 &&
            Object.keys(negatievePGA).length < 1;
    }

    public ngOnInit(): void {
        this.dataService.aangifteBerekening.subscribe((value) => {
            this.berekening = value;
            if (this.berekening.opbouwPGA !== undefined) {
                const pga = this.berekening.opbouwPGA;
                const specifiekeZorgkosten = pga?.specifiekeZorgkosten || 0;
                const aftrekbareGiften = pga?.aftrekbareGiften || 0;
                const scholingsuitgaven = pga?.scholingsuitgaven || 0;
                const restantPgaVoorgaandeJaren = pga?.restantPgaVoorgaandeJaren || 0;
                const onderhoudsverplichtingen = pga?.onderhoudsverplichtingen || 0;
                const weekenduitgaven = pga?.weekenduitgaven || 0;
                this.optellingPga = specifiekeZorgkosten + aftrekbareGiften + scholingsuitgaven + restantPgaVoorgaandeJaren + onderhoudsverplichtingen + weekenduitgaven
                this.totaalPgaFg.patchValue({totaalPga: this.optellingPga});
            }
        });
        this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                const aangifte = incomeTaxEntity.aangifte;
                this.aangifte = aangifte;
                this.doBerekenNegatievePga();
                this.disableFormGroup(this.totaalPgaFg);
                this.disableFormGroup(this.terugOntvangenPgaFg);
            }
        });
    }

    disableFormGroup(formGroup: FormGroup | AbstractControl): void {
        if (formGroup instanceof FormGroup) {
            Object.keys(formGroup.controls).forEach((key) => {
                const control = formGroup.get(key);
                if (control instanceof FormControl) {
                    control.disable();
                } else if (control instanceof FormGroup) {
                    this.disableFormGroup(control);
                }
            });
        }
    }

    private doBerekenNegatievePga() {

        const pga = this.aangifte.box1?.negatievePersoonsgebondenAftrek;
        const terugontvangenAlimentatie = pga?.terugontvangenAlimentatie || 0;
        const inkomstenSpecifiekeZiektekosten = pga?.inkomstenSpecifiekeZiektekosten || 0;
        const inkomstenOverigGiften = pga?.inkomstenOverigGiften || 0;
        const inkomstenOverigStudiekosten = pga?.inkomstenOverigStudiekosten || 0;

        const pgaPartner = this.aangifte.partnerAangifte?.box1?.negatievePersoonsgebondenAftrek;
        const terugontvangenAlimentatiePartner = pgaPartner?.terugontvangenAlimentatie || 0;
        const inkomstenSpecifiekeZiektekostenPartner = pgaPartner?.inkomstenSpecifiekeZiektekosten || 0;
        const inkomstenOverigStudiekostenPartner = pgaPartner?.inkomstenOverigGiften || 0;
        const inkomstenOverigGiftenPartner = pgaPartner?.inkomstenOverigStudiekosten || 0;

        const totaalNegatievePga = terugontvangenAlimentatie + inkomstenSpecifiekeZiektekosten + inkomstenOverigGiften + inkomstenOverigStudiekosten +
            terugontvangenAlimentatiePartner + inkomstenSpecifiekeZiektekostenPartner + inkomstenOverigGiftenPartner + inkomstenOverigGiftenPartner;


        this.terugOntvangenPgaFg.patchValue({
            terugontvangenAlimentatieAangever: terugontvangenAlimentatie || 0,
            inkomstenSpecifiekeZiektekostenAangever: inkomstenSpecifiekeZiektekosten || 0,
            inkomstenOverigGiftenAangever: inkomstenOverigGiften || 0,
            inkomstenOverigStudiekostenAangever: inkomstenOverigStudiekosten || 0,
            terugontvangenAlimentatiePartner: terugontvangenAlimentatiePartner || 0,
            inkomstenSpecifiekeZiektekostenPartner: inkomstenSpecifiekeZiektekostenPartner || 0,
            inkomstenOverigGiftenPartner: inkomstenOverigGiftenPartner || 0,
            inkomstenOverigStudiekostenPartner: inkomstenOverigStudiekostenPartner || 0,
            totaalNegatievePga: totaalNegatievePga || 0,
        });

    }
}

